import React, { useMemo, useState } from "react";
import { lowerCase, startCase } from "lodash";
import {
  Card,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardContent,
  FormControl,
  Autocomplete,
  Typography,
  TextField,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddIcon from "@material-ui/icons/Add";
import { useSelector } from "react-redux";
import { empleadosApi } from "src/services/empleadosApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  hidden: {
    display: "none",
  },
});

// ----------------------------------------------------------------------

export default function EmpleadosAsociadosPlan({ plan }) {
  const [empleadosAsociados, setEmpleadosAsociados] = useState([]);
  const classes = useStyles();
  const { sucursal: sucursalObtenida, rol: rolObtenido } = useSelector(
    (state) => state.auth.user,
  );

  const [eliminarEmpleadoPlan] = empleadosApi.useDeleteEmpleadosPlanMutation();
  const [agregarEmpleadosPlan] = empleadosApi.useAddEmpleadosPlanMutation();

  const { data: empleadosPlan = [], isLoading: isLoadingEmpleadosPlan } =
    empleadosApi.useGetEmpleadosPlanQuery(plan.id);

  const [rutEmpleadoAdicional, setRutEmpleadoAdicional] = useState("");
  const { data: empleadosData } = empleadosApi.useGetEmpleadosActivosQuery({
    id: sucursalObtenida.refSucursal,
    estado: "ALL",
  });

  const autorizado = rolObtenido !== "EMPLEADO";

  const [empleadoAdicionalSelect, setEmpleadoAdicionalSelect] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const empleados = empleadosData ?? [];
  const empleadoOptions = useMemo(
    () =>
      (empleadosData ?? []).map((item) => ({
        label: startCase(lowerCase(item.nombre + " " + item.apellidos)),
        value: item.rut,
      })),
    [empleadosData],
  );

  const handleEliminarEmpleado = async (empleadoId) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar el empleado?",
      confirmButtonText: "Si, eliminar",
    }).fire();

    if (!result.isConfirmed) return;

    try {
      await eliminarEmpleadoPlan(empleadoId);
      enqueueSnackbar("Empleado eliminado correctamente.", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        "Ocurrió un error en el servidor. Inténtelo nuevamente más tarde.",
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <Card>
      <Typography variant="h5" style={{ textAlign: "center" }} gutterBottom>
        Empleados Asociados
      </Typography>
      <CardContent>
        {(rolObtenido === "ADMIN" || rolObtenido === "SUPER_ADMIN") && (
          <Card>
            <CardContent>
              <Grid container>
                <Grid item xs={10} md={10}>
                  <FormControl fullWidth style={{ marginTop: "30px" }}>
                    <Autocomplete
                      disabled={!autorizado}
                      value={empleadoAdicionalSelect}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, newValue) => {
                        if (newValue !== null) {
                          setRutEmpleadoAdicional(
                            empleados.find(
                              (empleado) => empleado.rut === newValue.value,
                            ),
                          );
                          setEmpleadoAdicionalSelect(newValue);
                        } else {
                          setRutEmpleadoAdicional("");
                          setEmpleadoAdicionalSelect(null);
                        }
                      }}
                      fullWidth
                      id="combo-box-empleados"
                      options={empleadoOptions}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          type="rut"
                          label="Empleados Adicionales"
                          required
                          value={rutEmpleadoAdicional}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2} md={2} style={{ marginTop: "30px" }}>
                  <Button
                    fullWidth
                    style={{ height: "100%" }}
                    variant="outlined"
                    onClick={() => {
                      if (!rutEmpleadoAdicional) {
                        enqueueSnackbar("Debe de seleccionar un empleado", {
                          variant: "error",
                        });
                        return;
                      }

                      if (plan.supervisor === rutEmpleadoAdicional.rut) {
                        enqueueSnackbar(
                          "No es posible agregar este empleado porque es supervisor.",
                          {
                            variant: "error",
                          },
                        );
                      } else if (
                        plan.refEmpleado === rutEmpleadoAdicional.rut
                      ) {
                        enqueueSnackbar(
                          "No es posible agregar este empleado porque es el encargado de la tarea.",
                          {
                            variant: "error",
                          },
                        );
                      } else {
                        let existe = empleadosAsociados.filter(
                          (e) => e.rut === rutEmpleadoAdicional.rut,
                        )[0];

                        let yaExiste = empleadosPlan.filter(
                          (e) => e.rut === rutEmpleadoAdicional.rut,
                        )[0];
                        if (existe || yaExiste) {
                          enqueueSnackbar(
                            "El empleado seleccionado ya fue agregado.",
                            {
                              variant: "error",
                            },
                          );
                        } else {
                          agregarEmpleadosPlan({
                            idTarea: plan.id,
                            empleadoObtenido: rutEmpleadoAdicional.rut,
                          })
                            .unwrap()
                            .then(() => {
                              empleadosAsociados.push(rutEmpleadoAdicional);
                              setEmpleadoAdicionalSelect(null);
                              enqueueSnackbar(
                                "Empleado agregado correctamente.",
                                {
                                  variant: "success",
                                },
                              );
                            })
                            .catch(() => {
                              enqueueSnackbar(
                                "Ocurrió un error en el servidor. Inténtelo nuevamente más tarde.",
                                {
                                  variant: "error",
                                },
                              );
                            });
                        }
                      }
                    }}
                  >
                    <AddIcon /> Agregar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        <Card style={{ padding: "1rem" }}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre Empleado</TableCell>
                  {(rolObtenido === "ADMIN" ||
                    rolObtenido === "SUPER_ADMIN") && <TableCell></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isLoadingEmpleadosPlan &&
                  empleadosPlan.map((row, idx) => (
                    <TableRow key={"empleadosAsociados_" + idx}>
                      <TableCell component="th" scope="row">
                        {row.nombreEmpleado + " " + row.apellidosEmpleado}
                      </TableCell>
                      {(rolObtenido === "ADMIN" ||
                        rolObtenido === "SUPER_ADMIN") && (
                        <TableCell>
                          <Button
                            color="error"
                            onClick={() => handleEliminarEmpleado(row.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </CardContent>
    </Card>
  );
}
